import type { TFunction } from 'i18next'
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { type Locale, i18n, localeMapping } from '../locales/i18n'

interface FunkitI18nValue {
  /** Translate against the SDK's private i18next instance. */
  t: TFunction
  locale: Locale
}

// Bound once so `t` survives destructuring (i18next's `t` is an instance method).
// Also serves trees mounted without <FunkitProvider> (unit tests, Storybook),
// so screens can call useFunkitTranslation unconditionally.
const DEFAULT_VALUE: FunkitI18nValue = { t: i18n.t.bind(i18n), locale: 'en' }

const FunkitI18nContext = createContext<FunkitI18nValue>(DEFAULT_VALUE)

export interface FunkitI18nProviderProps {
  children: ReactNode
  /** UI language. Defaults to English. */
  locale?: Locale
}

/**
 * Translation layer for `@funkit/connect-rn`. Keeps the SDK's copy on its
 * private i18next instance and exposes `t` via {@link useFunkitTranslation}.
 * Internal — host apps mount {@link FunkitProvider}, which passes `locale` down.
 */
export function FunkitI18nProvider({
  children,
  locale = 'en',
}: FunkitI18nProviderProps) {
  // `changeLanguage` resolves async, so the applied language is tracked in
  // state via the instance's own event — subscribers re-render only once the
  // new resources are actually active (a fresh fixed `t` below).
  const [language, setLanguage] = useState(i18n.language)
  useEffect(() => {
    i18n.on('languageChanged', setLanguage)
    return () => i18n.off('languageChanged', setLanguage)
  }, [])

  useEffect(() => {
    if (i18n.language !== localeMapping[locale]) {
      void i18n.changeLanguage(localeMapping[locale])
    }
  }, [locale])

  const t = useMemo<TFunction>(() => i18n.getFixedT(language), [language])
  const value = useMemo<FunkitI18nValue>(() => ({ t, locale }), [t, locale])
  return (
    <FunkitI18nContext.Provider value={value}>
      {children}
    </FunkitI18nContext.Provider>
  )
}

/** Read the SDK's translation function (and active locale). */
export function useFunkitTranslation(): FunkitI18nValue {
  return useContext(FunkitI18nContext)
}
